// ─── Talking to the server ───────────────────────────────────────────────────
//
// One module, and the ONLY place in the client where a date crosses the wire.
//
// JSON has no date type, so everything arrives as an ISO string. Parsing at
// the point of use means some component eventually renders a string where a
// Date was expected — and the failure surfaces three screens away as "1970"
// or "Invalid Date" rather than where the mistake was made. So every response
// is parsed here, once, and the rest of the client only ever sees `Date`.
//
// ── Whose clock ─────────────────────────────────────────────────────────────
//
// Instants go over the wire; WALL CLOCKS come back out. Every function that
// touches a date takes the business's `timeZone` and converts, because the
// components render a Date through the viewer's local getters — so a Date
// handed to them must already read as the business's clock.
//
// The zone is a REQUIRED PARAMETER rather than module state set at startup.
// Both would work; only one is checked. A forgotten argument is a compile
// error, whereas a forgotten `configure()` call is an hour's silent drift for
// anybody whose device is set elsewhere — which is the bug this arrangement
// exists to close, found in a real browser on 2026-09-10.
//
// ── On errors ───────────────────────────────────────────────────────────────
//
// A failed request throws `ApiError` carrying the server's own message. The
// server sends a real reason for the cases a person can act on — a slot that
// has just gone, a name that is missing — and replacing those with "Something
// went wrong" would take away the only thing that tells them what to do next.

import { toZonedWallClock, fromZonedWallClock } from "@nulife/booking-ui";
import { createApiClient, ApiError } from "@nulife/app-ui";
import type { CalendarPort } from "@nulife/calendar-ui";

/**
 * The transport, from `@nulife/app-ui`.
 *
 * It was forty lines here until 2026-09-15, when the Memorial Hall committee
 * app became the second product that needed exactly the same three decisions:
 * a dropped connection is not a server error, the server's own message
 * survives, and a 401 is announced so a session that ends mid-use returns the
 * app to the sign-in form. See that module's header for why those in
 * particular are the part worth sharing.
 *
 * What stayed here is everything below: this product's routes, its shapes, and
 * the timezone conversion — the zone belongs to the tenant, and the components
 * render a Date through the viewer's local getters, so the conversion has to
 * happen where the routes are known.
 */
const client = createApiClient();
const { request, post, send } = client;

/**
 * Called when the server says 401.
 *
 * A session can end while the diary is open — she signs out on her phone, or a
 * password change ends it. Without this, every panel would simply fail and the
 * screen would look broken rather than signed out.
 */
export const whenSignedOut = client.whenSignedOut;

/** Re-exported so `api.ApiError` still resolves for every caller, and so
 *  `slotTaken` stays reachable where a booking is being taken. */
export { ApiError };

// ─── What the server sends ───────────────────────────────────────────────────

export type Service = {
  id: string;
  name: string;
  durationMinutes: number;
  description: string | null;
  priceMinor: number | null;
  currency: string | null;
};

/**
 * A bookable time.
 *
 * `startsAt` is the WALL CLOCK, for the components. `instant` is the real
 * moment the server sent, carried alongside so the booking never has to be
 * re-derived from the wall clock.
 *
 * That matters on the morning the clocks go back, when a wall clock names two
 * instants an hour apart and any inverse has to guess. Keeping the original
 * means there is nothing to guess: the customer is booked at the moment they
 * were offered, not at one that merely looks the same.
 */
export type Slot = { resourceId: string; startsAt: Date; endsAt: Date; instant: Date };

export type Booking = {
  id: string;
  /** Wall clock, for the components. */
  startsAt: Date;
  endsAt: Date;
  /** The real moment, kept so a drag can resolve the ambiguous hour against
   *  where the booking already was rather than guessing. */
  instant: Date;
  status: "requested" | "confirmed" | "declined" | "cancelled" | "blocked";
  blockReason: string | null;
  notes: string | null;
  priceMinor: number | null;
  currency: string | null;
  personName: string | null;
  serviceName: string | null;
};

export type MonthDayFacts = {
  /** "2027-03-01", in the BUSINESS's timezone — which is why it is a string
   *  and not a Date. A Date would be re-interpreted in the viewer's zone, and
   *  a customer in Spain would see the calendar shifted by a day. */
  date: string;
  working: boolean;
  openSlots: number;
  /** Business surface only. The customer route does not send this field at
   *  all, rather than sending zero — booking volume is not a customer's
   *  business. */
  bookings?: number;
};

export type HoursRule = { weekday: number; startMinute: number; endMinute: number };

export type Client = {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  /** Bookings that still stand — declined and cancelled are not visits. */
  bookings: number;
  lastVisit: string | null;
  nextVisit: string | null;
  lastService: string | null;
};

// ─── Shared ──────────────────────────────────────────────────────────────────

export const getServices = () => request<Service[]>("/services");

// ─── Signing in ──────────────────────────────────────────────────────────────

export type Me = {
  signedIn: boolean;
  name?: string;
  email?: string | null;
  /** "Owner/Admin" or "Staff", for showing a person what they are signed in
   *  as. Absent when nobody is signed in. */
  role?: "Owner/Admin" | "Staff";
  /**
   * Whether this account may change business setup and admin settings.
   *
   * Used to stop OFFERING what cannot be done — a hidden button is a courtesy,
   * never the permission check. The server refuses regardless, and the
   * integration suite proves it does.
   */
  canConfigure?: boolean;
  /** What the business is called, from the database. */
  businessName?: string | null;
};

/** Who is signed in, if anyone. Never throws for "nobody" — that is an answer,
 *  not a failure, and the diary asks it on every load. */
export const getMe = () => request<Me>("/auth/me");

export const signIn = (email: string, password: string) =>
  post<{ name: string; email: string | null }>("/auth/sign-in", { email, password });

export const signOut = () => post<{ ok: true }>("/auth/sign-out", {});

export type Config = {
  mode: "request" | "instant";
  timeZone: string;
  businessName: string;
  /** The tenant's palette, for `data-nl-theme`. Null means the platform
   *  default. */
  theme: string | null;
};

/** Fetched before anything else: nothing can render a time until it knows
 *  whose clock to render it on. */
export const getSettings = () => request<Config>("/settings");

export const getHours = () => request<HoursRule[]>("/hours");

export async function getAvailability(
  serviceId: string, from: Date, to: Date, timeZone: string,
): Promise<Slot[]> {
  const q = new URLSearchParams({
    // `from`/`to` are the browser's own window ("today", "in 90 days"), which
    // are genuine instants and go as they are.
    serviceId, from: from.toISOString(), to: to.toISOString(),
  });
  const raw = await request<{ resourceId: string; startsAt: string; endsAt: string }[]>(
    `/availability?${q}`,
  );
  return raw.map(s => ({
    resourceId: s.resourceId,
    startsAt: toZonedWallClock(new Date(s.startsAt), timeZone),
    endsAt: toZonedWallClock(new Date(s.endsAt), timeZone),
    instant: new Date(s.startsAt),
  }));
}

// ─── Customer ────────────────────────────────────────────────────────────────

export const getCustomerMonth = (serviceId: string, year: number, month: number) =>
  request<MonthDayFacts[]>(
    `/customer/month?${new URLSearchParams({ serviceId, year: String(year), month: String(month) })}`,
  );

/**
 * Ask for an appointment.
 *
 * `clientActionId` is generated ONCE per tap by the caller and reused on every
 * retry — that is what makes a dropped connection safe. Minting one here would
 * produce a different id each attempt and defeat the whole mechanism, so it is
 * required rather than defaulted.
 */
export const requestBooking = (input: {
  serviceId: string;
  /**
   * The wall clock the customer chose, as the journey handed it back.
   *
   * Sending this as though it were an instant would book the appointment an
   * hour or two out — the display bug in the other direction, and this time
   * written to the database.
   */
  startsAt: Date;
  /**
   * The instant that wall clock came from, when it is known.
   *
   * Always known here, because the slot was fetched as an instant — so the
   * ambiguous hour on the morning the clocks go back costs nothing. Optional
   * only so a caller without one still has a correct, if guessing, path.
   */
  instant?: Date;
  clientActionId: string;
  details: { personId?: string | null; fullName: string; phone: string; email?: string; notes?: string };
}, timeZone: string) =>
  post<{ bookingId: string; status: "requested" | "confirmed"; personId: string }>(
    "/customer/bookings",
    {
      ...input,
      instant: undefined,
      startsAt: (input.instant
        ?? fromZonedWallClock(input.startsAt, timeZone)).toISOString(),
    },
  );

// ─── Business ────────────────────────────────────────────────────────────────

export const getBusinessMonth = (serviceId: string, year: number, month: number) =>
  request<Required<MonthDayFacts>[]>(
    `/business/month?${new URLSearchParams({ serviceId, year: String(year), month: String(month) })}`,
  );

export async function getBookings(from: Date, to: Date, timeZone: string): Promise<Booking[]> {
  // The window is built from wall-clock dates the calendar is showing, so it
  // converts on the way out too — otherwise "this week" would be the wrong
  // week's worth of hours for a viewer in another zone.
  const q = new URLSearchParams({
    from: fromZonedWallClock(from, timeZone).toISOString(),
    to: fromZonedWallClock(to, timeZone).toISOString(),
  });
  const raw = await request<(Omit<Booking, "startsAt" | "endsAt"> & { startsAt: string; endsAt: string })[]>(
    `/business/bookings?${q}`,
  );
  return raw.map(b => ({
    ...b,
    startsAt: toZonedWallClock(new Date(b.startsAt), timeZone),
    endsAt: toZonedWallClock(new Date(b.endsAt), timeZone),
    instant: new Date(b.startsAt),
  }));
}

/**
 * A drag on the calendar.
 *
 * `startsAt` is where the block was dropped — a wall clock the grid computed
 * from pixels, so there is no original instant for it and the inverse really
 * is needed. `wasAt` is the booking's current instant, passed as the hint: if
 * the drop lands in the repeated hour, the nearer of the two candidates is
 * overwhelmingly the one the person meant, since they dragged it a few rows
 * rather than an hour.
 */
export const moveBooking = (id: string, startsAt: Date, timeZone: string, wasAt?: Date) =>
  post<{ id: string; startsAt: string; endsAt: string }>(
    `/business/bookings/${id}/move`,
    { startsAt: fromZonedWallClock(startsAt, timeZone, { near: wasAt }).toISOString() },
  );

export const setBookingStatus = (id: string, status: "confirmed" | "declined" | "cancelled") =>
  post<{ changed: boolean; status: string | null }>(`/business/bookings/${id}/status`, { status });

export const blockTime = (startsAt: Date, endsAt: Date, timeZone: string, reason?: string) =>
  post<{ id: string }>("/business/block", {
    startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
    endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
    reason,
  });

/**
 * Every client, in one request.
 *
 * Deliberately not a search endpoint — filtering happens in the browser. For a
 * one-chair salon the whole list is small, and a list already in hand filters
 * instantly and keeps working on a bad connection. See `matchClients`.
 */
export const getClients = () => request<Client[]>("/business/clients");

/** Add a client by hand. `created` is false when the phone number was already
 *  on file, so the screen can say "you already have them" rather than claiming
 *  an addition that did not happen. */
export const addClient = (draft: { fullName: string; phone: string; email?: string }, clientActionId: string) =>
  post<{ id: string; fullName: string; phone: string | null; created: boolean }>(
    "/business/clients", { ...draft, clientActionId },
  );

/**
 * Take a booking on the salon's behalf — a phone call, a walk-in.
 *
 * Goes in CONFIRMED, because Carrie taking the booking is the approval. The
 * instant is sent, not the wall clock: `slotInstant` is the one the
 * availability engine offered, so nothing has to be re-derived.
 */
export const addBooking = (input: {
  personId: string;
  serviceId: string;
  slotInstant: Date;
  clientActionId: string;
  notes?: string;
}) =>
  post<{ bookingId: string; personId: string; status: string }>("/business/bookings", {
    personId: input.personId,
    serviceId: input.serviceId,
    startsAt: input.slotInstant.toISOString(),
    clientActionId: input.clientActionId,
    notes: input.notes,
  });

export type ClientVisit = {
  id: string;
  startsAt: Date;
  endsAt: Date;
  status: string;
  serviceName: string | null;
  priceMinor: number | null;
  currency: string | null;
  /** What happened at THAT visit — the colour formula, what was done. */
  notes: string | null;
};

export type ClientDetail = {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  /** The standing note: allergies, preferences. Edited, not appended. */
  notes: string | null;
  history: ClientVisit[];
  activity: { at: Date; action: string; label: string | null }[];
};

/** One client, with their notes and their whole history. */
export async function getClient(id: string, timeZone: string): Promise<ClientDetail> {
  const raw = await request<Omit<ClientDetail, "history" | "activity"> & {
    history: (Omit<ClientVisit, "startsAt" | "endsAt"> & { startsAt: string; endsAt: string })[];
    activity: { at: string; action: string; label: string | null }[];
  }>(`/business/clients/${id}`);
  return {
    ...raw,
    history: raw.history.map(v => ({
      ...v,
      startsAt: toZonedWallClock(new Date(v.startsAt), timeZone),
      endsAt: toZonedWallClock(new Date(v.endsAt), timeZone),
    })),
    activity: raw.activity.map(a => ({ ...a, at: toZonedWallClock(new Date(a.at), timeZone) })),
  };
}

/**
 * Change a client's details.
 *
 * Only send what you mean to change. An absent field is left alone; the
 * server builds its patch from the keys present, precisely so a note edit
 * cannot wipe a phone number.
 */
export const saveClient = (id: string, patch: Partial<{ fullName: string; phone: string; email: string | null; notes: string | null }>) =>
  request<{ changed: boolean; fields: string[] }>(`/business/clients/${id}`, {
    method: "PATCH", body: JSON.stringify(patch),
  });

/**
 * What happened at a visit. Returns false when the text was unchanged.
 *
 * Through `send()` rather than `request()` because a 404 here is an ANSWER,
 * not a failure — the module returns null both for "no such booking" and for
 * "nothing changed", and either way there is nothing to report as saved. That
 * needs the response, which is what `send` hands back; it still announces a
 * 401, which this path did not when it called `fetch` itself.
 */
export async function saveVisitNotes(bookingId: string, notes: string): Promise<boolean> {
  const res = await send(`/business/bookings/${bookingId}/notes`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ notes }),
  });
  // 404 here means "nothing changed" as well as "no such booking" — the
  // module returns null for both. Either way there is nothing to report as
  // saved, which is what the caller needs to know.
  if (res.status === 404) return false;
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not save the note.");
  }
  return true;
}

export type CalendarEntryItem = {
  /** For one occurrence of a repeating entry this is `<entry id>@<instant>` —
   *  the date is part of the identity, because that is what "cancel this one"
   *  has to name. */
  id: string;
  source: string;
  title: string;
  subtitle: string | null;
  startsAt: Date;
  endsAt: Date;
  allDay: boolean;
  status: string | null;
  editable: boolean;
  /** Set when this is one occurrence of a series. `description` is already in
   *  words ("every week on Tuesday") — the screen never parses the rule.
   *  `moved` means this one date has been taken off the rule. */
  series: { id: string; rule: string; description: string; moved: boolean } | null;
};

/* The repeat list moved to `@nulife/calendar-ui` with the calendar screen
 * itself on 2026-09-15 — it is a list of RFC 5545 rules and a UI choice about
 * which of them to offer, neither of which is Crafting Comb's. Imported from
 * there by anything that needs it. */

/**
 * Everything on between two moments — the salon's own diary entries and the
 * bookings Booking owns, projected.
 *
 * `failures` comes back rather than throwing. A calendar that renders empty
 * because one source failed is claiming nothing is on, and the screen has to
 * be able to say which part is missing instead.
 */
export async function getCalendar(from: Date, to: Date, timeZone: string): Promise<{
  items: CalendarEntryItem[];
  failures: { source: string; error: string }[];
}> {
  const q = new URLSearchParams({
    from: fromZonedWallClock(from, timeZone).toISOString(),
    to: fromZonedWallClock(to, timeZone).toISOString(),
  });
  const raw = await request<{
    items: (Omit<CalendarEntryItem, "startsAt" | "endsAt"> & { startsAt: string; endsAt: string })[];
    failures: { source: string; error: string }[];
  }>(`/business/calendar?${q}`);
  return {
    items: raw.items.map(i => ({
      ...i,
      startsAt: toZonedWallClock(new Date(i.startsAt), timeZone),
      endsAt: toZonedWallClock(new Date(i.endsAt), timeZone),
    })),
    failures: raw.failures,
  };
}

export const addCalendarEntry = (entry: {
  title: string; startsAt: Date; endsAt: Date; location?: string;
  allDay?: boolean; description?: string;
  /** An RRULE line, or absent for a one-off. The server validates it and
   *  answers with a sentence the form can show. */
  recurrence?: string;
}, timeZone: string) =>
  post<{ id: string }>("/business/calendar/entries", {
    title: entry.title,
    startsAt: fromZonedWallClock(entry.startsAt, timeZone).toISOString(),
    endsAt: fromZonedWallClock(entry.endsAt, timeZone).toISOString(),
    allDay: entry.allDay === true,
    location: entry.location,
    description: entry.description,
    recurrence: entry.recurrence,
  });

/**
 * Call off one date of a repeating entry, or put it back.
 *
 * The id is an occurrence id and carries an `@` and the colons of an ISO
 * instant, so it is encoded — unencoded it would be read as a different path.
 */
export const skipOccurrence = (id: string) =>
  post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/skip`, {});

export const restoreOccurrence = (id: string) =>
  post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/restore`, {});

/**
 * Move ONE occurrence of a repeating entry, leaving the series alone.
 *
 * Deliberately a different call from `moveCalendarEntry`, not a flag on it.
 * The two do genuinely different things, and the one that could move a year of
 * meetings should never be reachable by getting an argument wrong.
 */
export const moveCalendarOccurrence = (
  id: string, startsAt: Date, endsAt: Date, timeZone: string,
) =>
  post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/move`, {
      startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
      endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
    });

/** Put one moved occurrence back on its series' rule. */
export const resetOccurrence = (id: string) =>
  post<{ changed: boolean }>(
    `/business/calendar/occurrences/${encodeURIComponent(id)}/reset`, {});

/** Move an entry the calendar OWNS. A projected booking is moved through
 *  Booking's own route — the server refuses this one for anything else. */
export const moveCalendarEntry = (id: string, startsAt: Date, endsAt: Date, timeZone: string) =>
  request<{ id: string }>(`/business/calendar/entries/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      startsAt: fromZonedWallClock(startsAt, timeZone).toISOString(),
      endsAt: fromZonedWallClock(endsAt, timeZone).toISOString(),
    }),
  });

export const runningLate = (minutes: number) =>
  post<{ notified: number }>("/business/running-late", { minutes });

export const addService = (draft: {
  name: string; durationMinutes: number; description?: string; priceMinor?: number | null;
}) => post<{ id: string }>("/business/services", draft);

/** Archived rather than deleted — past bookings keep the service they were
 *  made for. The setup screen says so on the button. */
/**
 * Change a service in place — name, duration, price.
 *
 * `priceMinor: null` clears a price, which is a real thing to want for a
 * service quoted on consultation. Sent explicitly rather than omitted, because
 * the server treats an absent field as "leave it alone".
 */
export const saveService = (id: string, patch: {
  name: string; durationMinutes: number; priceMinor: number | null;
}) =>
  request<{ id: string }>(`/business/services/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(patch),
  });

export const removeService = (id: string) =>
  request<{ changed: boolean }>(`/business/services/${id}`, { method: "DELETE" });

export const saveHours = (hours: { weekday: number; open: boolean; from: string; to: string }[]) =>
  request<{ ok: true }>("/business/hours", { method: "PUT", body: JSON.stringify({ hours }) });

export const saveMode = (mode: "request" | "instant") =>
  request<{ mode: string }>("/business/mode", { method: "PUT", body: JSON.stringify({ mode }) });

// ─── Capture ──────────────────────────────────────────────────────────────

export type CaptureFile = {
  id: string; filename: string; mimeType: string; url: string;
  thumbnailUrl: string | null; size: number;
};

export type Capture = {
  id: string; title: string | null; note: string | null; tag: string | null;
  createdAt: Date; updatedAt: Date; files: CaptureFile[];
  /** When it was deleted, or null if it has not been. Turned into "N days
   *  left" on the deleted list — a window nobody can see the end of is one
   *  people do not trust. */
  archivedAt: Date | null;
};

export type CaptureSection = { tag: string; tagKey: string; count: number };

// ── Notes and files against a client (issue #10) ───────────────────────────

export type ClientNote = { id: string; body: string; createdAt: Date; updatedAt: Date };
export type ClientFile = {
  id: string; filename: string; mimeType: string; url: string;
  thumbnailUrl: string | null; size: number; label: string | null; createdAt: Date;
};

export async function getClientRecords(personId: string): Promise<{
  notes: ClientNote[]; files: ClientFile[];
}> {
  const raw = await request<{ notes: any[]; files: any[] }>(
    `/business/clients/${encodeURIComponent(personId)}/records`);
  return {
    notes: raw.notes.map(n => ({ ...n, createdAt: new Date(n.createdAt), updatedAt: new Date(n.updatedAt) })),
    files: raw.files.map(f => ({ ...f, createdAt: new Date(f.createdAt) })),
  };
}

export const addClientNote = (personId: string, body: string) =>
  post<ClientNote>(`/business/clients/${encodeURIComponent(personId)}/notes`, {
    body, clientActionId: crypto.randomUUID(),
  });

export const editClientNote = (noteId: string, body: string) =>
  request<ClientNote>(`/business/notes/${encodeURIComponent(noteId)}`, {
    method: "PATCH", body: JSON.stringify({ body }),
  });

export const removeClientNote = (noteId: string) =>
  request<{ changed: boolean }>(`/business/notes/${encodeURIComponent(noteId)}`,
    { method: "DELETE" });

/**
 * Attach files to a client.
 *
 * Goes through `send()` rather than `request()` for the same reason
 * `uploadCapture` does: a multipart body must not carry a JSON content-type,
 * which `request()` always sets. `send()` still announces a 401 — which this
 * path did not, when it called `fetch` directly, so a session that ended
 * mid-upload left a raw error on the screen instead of the sign-in form.
 */
export async function attachClientFiles(
  personId: string, files: File[], label?: string,
): Promise<ClientFile[]> {
  const form = new FormData();
  for (const f of files) form.append("items", f);
  if (label) form.append("label", label);
  form.append("clientActionId", crypto.randomUUID());
  const res = await send(
    `/business/clients/${encodeURIComponent(personId)}/files`,
    { method: "POST", credentials: "include", body: form },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not attach that.");
  }
  const raw = await res.json() as { files: any[] };
  return raw.files.map(f => ({ ...f, createdAt: new Date(f.createdAt) }));
}

export const labelClientFile = (fileId: string, label: string | null) =>
  request<ClientFile>(`/business/files/${encodeURIComponent(fileId)}`, {
    method: "PATCH", body: JSON.stringify({ label }),
  });

export const removeClientFile = (fileId: string) =>
  request<{ changed: boolean }>(`/business/files/${encodeURIComponent(fileId)}`,
    { method: "DELETE" });

async function parseCapture(raw: any): Promise<Capture> {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
    archivedAt: raw.archivedAt ? new Date(raw.archivedAt) : null,
  };
}

/** `tag: null` for the Inbox (untagged only); a string for that section;
 *  omitted for everything. See the server route's own comment for why
 *  those are kept as distinct shapes rather than one optional param. */
export function getCaptures(tag?: string | null) {
  const qs = tag === null ? "?inbox=1" : tag ? `?tag=${encodeURIComponent(tag)}` : "";
  return getCaptureList(`/business/captures${qs}`);
}

/**
 * What has been deleted and is still inside the retention window.
 *
 * `retentionDays` comes from the server rather than being written here, so the
 * window and the sentence on screen cannot drift apart — a screen promising 30
 * days over a job that purges after 14 is worse than no promise.
 */
export const getDeletedCaptures = () => getCaptureList("/business/captures?deleted=1");

/** Put one back. Its own route, not a flag on the edit — see the server. */
export const restoreCapture = (id: string) =>
  post<{ ok: true }>(`/business/captures/${encodeURIComponent(id)}/restore`, {});

/** Both list calls answer the same shape. `retentionDays` comes back from
 *  EVERY list, not only the deleted one, so the message shown after a delete
 *  can name the window too — the moment somebody most wants to know it. */
async function getCaptureList(path: string): Promise<{
  captures: Capture[]; retentionDays: number;
}> {
  const raw = await request<{ captures: any[]; retentionDays: number }>(path);
  return {
    captures: await Promise.all(raw.captures.map(parseCapture)),
    retentionDays: raw.retentionDays,
  };
}

export const getCaptureSections = () => request<CaptureSection[]>("/business/captures/sections");

/**
 * Upload a capture bundle. Goes through `send()` rather than `request()` — a
 * multipart body must NOT carry a `Content-Type: application/json` header,
 * which `request()` always sets whenever a body is present (same reason
 * `saveVisitNotes` above does, for its own special case).
 *
 * `clientActionId` is generated by the CALLER, once per capture attempt —
 * see the server route's own comment for why a retry needs the same id to
 * be treated as "already done" rather than a second capture.
 */
export async function uploadCapture(input: {
  note?: string; tag?: string; files?: File[]; clientActionId: string;
}): Promise<Capture> {
  const form = new FormData();
  form.set("clientActionId", input.clientActionId);
  if (input.note) form.set("note", input.note);
  if (input.tag) form.set("tag", input.tag);
  for (const f of input.files ?? []) form.append("items", f, f.name);

  const res = await send("/business/captures", { method: "POST", credentials: "include", body: form });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string })?.error ?? "Could not save the capture.");
  }
  return parseCapture(body);
}

/** "Rename" (title) and "Move" (tag — "" or omitted clears back to Inbox)
 *  both go through this, same one-endpoint shape as `saveClient`. */
export const saveCapture = (id: string, patch: Partial<{ title: string | null; note: string | null; tag: string | null }>) =>
  request<{ ok: true; changed: boolean }>(`/business/captures/${id}`, {
    method: "PATCH", body: JSON.stringify(patch),
  });

/** Rename a whole section — every capture currently filed under `tagKey`
 *  moves to `newTag` in one server-side bulk write. */
export const renameCaptureSection = (tagKey: string, newTag: string) =>
  request<{ ok: true; moved: number }>(`/business/captures/sections/${encodeURIComponent(tagKey)}`, {
    method: "PATCH", body: JSON.stringify({ tag: newTag }),
  });

/** Archived, not deleted — same convention as everything else here. */
export const deleteCapture = (id: string) =>
  request<{ ok: true; changed: boolean }>(`/business/captures/${id}`, { method: "DELETE" });

// ─── The calendar's transport ────────────────────────────────────────────────
//
// `CalendarSurface` in `@nulife/calendar-ui` is the whole calendar screen and
// imports no API module; it is handed these functions instead. They take and
// return WALL-CLOCK dates, and the zone conversion happens here because the
// zone belongs to the tenant and every other conversion in this product is
// already in this file.
//
// A factory rather than a constant: the zone is a REQUIRED ARGUMENT for the
// same reason it is everywhere else in this module — a forgotten argument is a
// compile error, where a forgotten `configure()` is an hour of silent drift.
//
// The result MUST be memoised by the caller. It is in the screen's fetch
// dependencies deliberately (see that prop's own note), so a fresh object each
// render is a fetch each render.
export const calendarPort = (timeZone: string): CalendarPort => ({
  list: (from, to) => getCalendar(from, to, timeZone),
  addEntry: entry => addCalendarEntry(entry, timeZone),
  skipOccurrence,
  restoreOccurrence,
  moveOccurrence: (id, startsAt, endsAt) =>
    moveCalendarOccurrence(id, startsAt, endsAt, timeZone),
  resetOccurrence,
  moveEntry: (id, startsAt, endsAt) => moveCalendarEntry(id, startsAt, endsAt, timeZone),
});
